import { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { authService } from '../services/auth.service';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import PageMeta from '../components/common/PageMeta';

// スキーマは動的に生成する必要があるため、関数として定義
const createRequestPasswordResetSchema = (t: (key: string, options?: any) => string) => z.object({
  email: z.string().email(t('auth.password_reset_invalid_email')),
});

const createResetPasswordSchema = (t: (key: string, options?: any) => string) => z.object({
  newPassword: z
    .string()
    .min(8, t('auth.password_reset_password_min_length'))
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/, t('auth.password_reset_password_requirements')),
  confirmPassword: z.string().min(8, t('auth.password_reset_password_min_length')),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: t('auth.password_reset_password_mismatch'),
  path: ['confirmPassword'],
});

type RequestPasswordResetFormData = z.infer<ReturnType<typeof createRequestPasswordResetSchema>>;
type ResetPasswordFormData = z.infer<ReturnType<typeof createResetPasswordSchema>>;

export default function PasswordReset() {
  const { t, i18n, ready } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [step, setStep] = useState<'request' | 'verify' | 'reset' | 'success'>('request');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const navigate = useNavigate();

  // デバッグ用: i18nの状態を確認（成功体験に基づく）
  useEffect(() => {
    console.log('[PasswordReset] i18n state on mount:', {
      isInitialized: i18n.isInitialized,
      language: i18n.language,
      languages: i18n.languages,
      hasResourceBundle_ja: i18n.hasResourceBundle('ja', 'translation'),
      hasResourceBundle_en: i18n.hasResourceBundle('en', 'translation'),
      ready: ready,
    });

    // リソースバンドルの直接確認（成功体験のパターン）
    if (i18n.isInitialized) {
      const resourceBundle = i18n.getResourceBundle(i18n.language, 'translation');
      const authObj = resourceBundle?.auth;
      console.log('[PasswordReset] Resource bundle check:', {
        currentLanguage: i18n.language,
        hasAuth: !!authObj,
        hasPasswordResetTitle: !!resourceBundle?.password_reset_title,
        passwordResetTitle: resourceBundle?.password_reset_title,
        passwordResetEmail: resourceBundle?.password_reset_email,
        passwordResetEmailPlaceholder: resourceBundle?.password_reset_email_placeholder,
        passwordResetRequestDescription: resourceBundle?.password_reset_request_description,
        // authオブジェクト内のキーも確認
        authPasswordResetTitle: authObj?.password_reset_title,
        authPasswordResetEmail: authObj?.password_reset_email,
        authPasswordResetEmailPlaceholder: authObj?.password_reset_email_placeholder,
        authPasswordResetRequestDescription: authObj?.password_reset_request_description,
        allPasswordResetKeys: resourceBundle ? Object.keys(resourceBundle).filter(key => key.startsWith('password_reset')).slice(0, 10) : null,
        allAuthKeys: authObj ? Object.keys(authObj).filter(key => key.startsWith('password_reset')).slice(0, 10) : null,
      });

      // 翻訳のテスト（指摘されたキーを重点的に確認）
      console.log('[PasswordReset] Translation test (重点確認):', {
        'password_reset_title': t('password_reset_title'),
        'password_reset_request_description': t('password_reset_request_description'),
        'password_reset_email': t('password_reset_email'),
        'password_reset_email_placeholder': t('password_reset_email_placeholder'),
        // authオブジェクト内のキーもテスト
        'auth.password_reset_title': t('auth.password_reset_title'),
        'auth.password_reset_request_description': t('auth.password_reset_request_description'),
        'auth.password_reset_email': t('auth.password_reset_email'),
        'auth.password_reset_email_placeholder': t('auth.password_reset_email_placeholder'),
      });
    }
  }, [i18n, t, ready]);

  // ページマウント時に言語設定を読み込む（Register.tsxと同じパターン）
  useEffect(() => {
    // Cookieから言語設定を読み込む関数
    const getCookie = (name: string): string | null => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
      return null;
    };

    // 言語設定を読み込む関数
    const loadLanguage = () => {
      // localStorageまたはCookieから言語設定を読み込む
      const savedLanguage = 
        localStorage.getItem('preferred_language') || 
        localStorage.getItem('i18nextLng') ||
        getCookie('preferred_language');
      
      // 有効な言語コードかチェック
      const validLanguages = ['ja', 'en', 'zh-CN', 'fr', 'es', 'de', 'pt', 'ru'];
      if (savedLanguage && validLanguages.includes(savedLanguage) && savedLanguage !== i18n.language) {
        i18n.changeLanguage(savedLanguage).catch((err) => {
          console.error('[PasswordReset] Failed to change language:', err);
        });
      }
    };

    // i18nの初期化が完了している場合は即座に実行、そうでない場合は初期化完了を待つ
    if (i18n.isInitialized) {
      loadLanguage();
    } else {
      const handleInitialized = () => {
        loadLanguage();
      };
      i18n.on('initialized', handleInitialized);
      return () => {
        i18n.off('initialized', handleInitialized);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 初回マウント時のみ実行

  // i18nの言語変更を監視
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      console.log('[PasswordReset] Language changed to:', lng);
    };
    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  const {
    register: registerRequest,
    handleSubmit: handleSubmitRequest,
    formState: { errors: errorsRequest },
  } = useForm<RequestPasswordResetFormData>({
    resolver: zodResolver(createRequestPasswordResetSchema(t)),
  });

  const {
    register: registerReset,
    handleSubmit: handleSubmitReset,
    formState: { errors: errorsReset },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(createResetPasswordSchema(t)),
  });

  // トークンがURLパラメータにある場合、検証する
  const { data: verifyResult, isError: verifyError } = useQuery({
    queryKey: ['passwordReset', 'verify', token],
    queryFn: () => authService.verifyPasswordResetToken(token!),
    enabled: !!token && step === 'request',
    retry: false,
  });

  useEffect(() => {
    if (token && verifyResult?.success) {
      setStep('reset');
    } else if (token && verifyError) {
      setError(t('auth.password_reset_invalid_token'));
      setStep('request');
    }
  }, [token, verifyResult, verifyError]);

  const requestMutation = useMutation({
    mutationFn: (data: RequestPasswordResetFormData) => authService.requestPasswordReset(data.email),
    onSuccess: (result) => {
      setSuccess(result.message);
      if (result.token) {
        // 開発環境ではトークンを表示
        setSuccess(`${result.message} ${t('auth.password_reset_token_label')}: ${result.token}`);
      }
      setError('');
    },
    onError: (err: any) => {
      setError(err.response?.data?.error?.message || t('auth.password_reset_request_failed'));
      setSuccess('');
    },
  });

  const resetMutation = useMutation({
    mutationFn: (data: ResetPasswordFormData) => authService.resetPassword(token!, data.newPassword),
    onSuccess: () => {
      setSuccess(t('auth.password_reset_reset_success'));
      setError('');
      setStep('success');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error?.message || t('auth.password_reset_reset_failed'));
      setSuccess('');
    },
  });

  const onSubmitRequest = (data: RequestPasswordResetFormData) => {
    requestMutation.mutate(data);
  };

  const onSubmitReset = (data: ResetPasswordFormData) => {
    resetMutation.mutate(data);
  };

  return (
    <>
      <PageMeta
        titleKey="password_reset.title"
        descriptionKey="password_reset.meta_description"
        path="password-reset"
        llmsDescribedBy
        robots="noindex,follow"
      />
      <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={6} lg={5}>
          <Card className="shadow-sm">
            <Card.Header className="text-center">
              <h2 className="mb-0">
                <i className="fas fa-key text-warning me-2"></i>
                {t('auth.password_reset_title')}
              </h2>
            </Card.Header>
            <Card.Body>
              {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {/* ステップ1: リセットリクエスト */}
              {step === 'request' && (
                <>
                  <p className="text-muted mb-4">
                    {t('auth.password_reset_request_description')}
                  </p>
                  <Form onSubmit={handleSubmitRequest(onSubmitRequest)}>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="password-reset-email">{t('auth.password_reset_email')}</Form.Label>
                      <Form.Control
                        id="password-reset-email"
                        name="email"
                        type="email"
                        placeholder={t('auth.password_reset_email_placeholder')}
                        {...registerRequest('email')}
                        isInvalid={!!errorsRequest.email}
                        autoComplete="username"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errorsRequest.email?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Button
                      type="submit"
                      variant="warning"
                      className="w-100"
                      disabled={requestMutation.isPending}
                    >
                      {requestMutation.isPending ? t('auth.password_reset_sending') : t('auth.password_reset_send_reset_link')}
                    </Button>
                  </Form>
                  <div className="mt-3 text-center">
                    <Button variant="link" onClick={() => navigate('/login')}>
                      {t('auth.password_reset_back_to_login')}
                    </Button>
                  </div>
                </>
              )}

              {/* ステップ2: パスワードリセット */}
              {step === 'reset' && (
                <>
                  <p className="text-muted mb-4">
                    {t('auth.password_reset_reset_description')}
                  </p>
                  <Form onSubmit={handleSubmitReset(onSubmitReset)}>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="password-reset-new-password">{t('auth.password_reset_new_password')}</Form.Label>
                      <Form.Control
                        id="password-reset-new-password"
                        name="newPassword"
                        type="password"
                        placeholder={t('auth.password_reset_password_placeholder')}
                        {...registerReset('newPassword')}
                        isInvalid={!!errorsReset.newPassword}
                        autoComplete="new-password"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errorsReset.newPassword?.message}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted">
                        <strong>{t('auth.password_reset_password_requirements_title')}</strong> {t('auth.password_reset_password_requirements_text')}
                      </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="password-reset-confirm-password">{t('auth.password_reset_confirm_password')}</Form.Label>
                      <Form.Control
                        id="password-reset-confirm-password"
                        name="confirmPassword"
                        type="password"
                        placeholder={t('auth.password_reset_confirm_password_placeholder')}
                        {...registerReset('confirmPassword')}
                        isInvalid={!!errorsReset.confirmPassword}
                        autoComplete="new-password"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errorsReset.confirmPassword?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Button
                      type="submit"
                      variant="warning"
                      className="w-100"
                      disabled={resetMutation.isPending}
                    >
                      {resetMutation.isPending ? t('auth.password_reset_resetting') : t('auth.password_reset_reset_button')}
                    </Button>
                  </Form>
                  <div className="mt-3 text-center">
                    <Button variant="link" onClick={() => navigate('/login')}>
                      {t('auth.password_reset_back_to_login')}
                    </Button>
                  </div>
                </>
              )}

              {/* ステップ3: 成功 */}
              {step === 'success' && (
                <>
                  <Alert variant="success">
                    <i className="fas fa-check-circle me-2"></i>
                    {t('auth.password_reset_success_message')}
                  </Alert>
                  <div className="text-center">
                    <Button variant="primary" onClick={() => navigate('/login')}>
                      {t('auth.password_reset_go_to_login')}
                    </Button>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
    </>
  );
}

